import { formatDate } from "@cp/toolkit";
import type { InboxMessageFragment } from "@qw/graphql";
import { parseEmailAddress } from "@qw/qw-common";
import type { ComponentProps, FC, ReactNode } from "react";

import { cn } from "../../utils";
import { Avatar } from "../avatar";
import { CollapsibleTrigger } from "../collapsible";
import { Gutter } from "../column";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../hover-card";
import { Icon } from "../icon";
import { MessageHeader } from "./message-header";

export interface MessageSenderProps extends ComponentProps<"header"> {
  message: InboxMessageFragment;
  isMessageOpen: boolean;
  /** The full header block is already showing, so the hover card would only repeat it. */
  displayHeaders?: boolean;
  actions?: ReactNode;
}

export const MessageSender: FC<MessageSenderProps> = ({
  message,
  isMessageOpen,
  displayHeaders = false,
  actions,
  className,
  ...props
}) => {
  const { date, from, preview, to } = message;
  const { firstName } = parseEmailAddress(from);
  const sender = firstName || "Unknown";

  const recipients = to
    .map(parseEmailAddress)
    .map(({ firstName }) => firstName)
    .join(", ");

  return (
    <header {...props} className={cn("flex flex-nowrap gap-2 items-start pr-3 text-left min-w-0", className)}>
      <CollapsibleTrigger
        asChild
        className={cn("flex-auto py-4 w-full cursor-pointer data-disabled:cursor-auto truncate")}
      >
        <div>
          <div className="flex min-w-0">
            <Gutter>
              <Avatar className="text-2xs">{sender[0]}</Avatar>
            </Gutter>

            <div className="flex flex-auto min-w-0 gap-4 items-center text-muted-foreground text-xs">
              <HoverCard openDelay={500}>
                <HoverCardTrigger asChild>
                  <div className="flex grow shrink min-w-0 gap-1.5 items-center truncate">
                    <span>{sender}</span>
                    {recipients[0] && (
                      <>
                        <Icon icon="arrow_right_alt" className="opacity-75" />
                        <span className="flex-auto truncate">{recipients}</span>
                      </>
                    )}
                  </div>
                </HoverCardTrigger>
                <HoverCardContent
                  hidden={displayHeaders && isMessageOpen}
                  align="start"
                  side="bottom"
                  sideOffset={16}
                  className="w-auto max-w-[calc(100dvw-6rem)] md:max-w-[60dvw] p-5 pt-4"
                >
                  <MessageHeader message={message} />
                </HoverCardContent>
              </HoverCard>

              <span className="flex-none ml-auto whitespace-nowrap">
                {formatDate(new Date(date), "LLL d, yyyy, h:mm aa")}
              </span>
            </div>
          </div>

          {preview && (
            <aside className="flex-auto group-data-[state=open]:hidden italic line-clamp-1 pl-(--gutter) mt-3 text-sm text-muted-foreground">
              {preview}
            </aside>
          )}
        </div>
      </CollapsibleTrigger>
      {actions}
    </header>
  );
};
